import {
  AssessmentInstrument,
  AssessmentOwnResponse,
  AssessmentWindow,
} from 'src/api/assessments';
import { contentLanguage, pick } from 'src/i18n/contentLanguage';

/* The loneliness measure's copy, English first and Norwegian bokmål when
   the browser is Norwegian (no em-dashes), and the small calculations the
   screens share. Product copy spells "Circle" with a capital C. */

/** The windows the screens show, in programme order. Follow-up comes with a template. */
export const SHOWN_WINDOWS: readonly AssessmentWindow[] = [
  'BEFORE',
  'MID',
  'AFTER',
];

export const WINDOW_LABEL: Record<AssessmentWindow, string> = pick(
  {
    BEFORE: 'At the start',
    MID: 'Midway',
    AFTER: 'After the programme',
    FOLLOW_UP: 'Follow-up',
  },
  {
    BEFORE: 'Ved oppstart',
    MID: 'Midtveis',
    AFTER: 'Etter programmet',
    FOLLOW_UP: 'Oppfølging',
  }
);

/** The line above the questions, per window. */
export const WINDOW_KICKER: Record<AssessmentWindow, string> = pick(
  {
    BEFORE: 'Before the programme starts',
    MID: 'Halfway through the programme',
    AFTER: 'After the programme',
    FOLLOW_UP: 'A few months after the programme',
  },
  {
    BEFORE: 'Før programmet starter',
    MID: 'Halvveis i programmet',
    AFTER: 'Etter programmet',
    FOLLOW_UP: 'Noen måneder etter programmet',
  }
);

export const TAB_LABEL = pick('Measure', 'Måling');

export const MEMBER_TITLE = pick('Your answers', 'Dine svar');
export const MEMBER_INTRO = pick(
  'Three questions, three times during the programme. The number goes from 3 (never) to 15 (very often). It is your own: the leader only sees one combined number for the group.',
  'Tre spørsmål, tre ganger i løpet av programmet. Tallet går fra 3 (aldri) til 15 (svært ofte). Det er ditt eget: lederen ser bare et samlet tall for gruppen.'
);

export const LEADER_TITLE = pick('The group as a whole', 'Gruppen samlet');
export const LEADER_INTRO = pick(
  "The average of the group's answers, from 3 (never lonely) to 15 (very often). You never see what any one person answered, and the numbers show only once at least five have answered.",
  'Snittet av gruppens svar, fra 3 (aldri ensom) til 15 (svært ofte). Du ser aldri hva den enkelte har svart, og tallene vises først når minst fem har svart.'
);

export const RESEARCH_LABEL = pick(
  'My answers may be used in research',
  'Svarene mine kan brukes i forskning'
);
export const RESEARCH_TEXT = pick(
  'Without your name, together with answers from other groups. You can switch this off at any time, also for answers you have already given.',
  'Uten navn, sammen med svar fra andre grupper. Du kan slå dette av når som helst, også for svar du allerede har gitt.'
);

export const CONSENT_SERVICE_TEXT = pick(
  'I understand that the answers are part of the programme. The institution offering the programme is responsible for the data, and Dunbar148 processes it on their behalf.',
  'Jeg forstår at svarene er en del av programmet. Institusjonen som tilbyr programmet er ansvarlig for opplysningene, og Dunbar148 behandler dem på deres vegne.'
);
export const CONSENT_RESEARCH_TEXT = pick(
  'My answers may be used in research, without my name. Optional, and I can change my mind later under Measure.',
  'Svarene mine kan brukes i forskning, uten navn. Valgfritt, og jeg kan ombestemme meg senere under Måling.'
);

export const SUBMIT_LABEL = pick('Send answers', 'Send svar');
export const LATER_LABEL = pick('Answer later', 'Svar senere');
export const FINAL_NOTE = pick(
  'The answers cannot be changed once sent.',
  'Svarene kan ikke endres etter at de er sendt.'
);
export const ANSWER_NOW_LABEL = pick('Answer now', 'Svar nå');
export const DURATION_NOTE = pick(
  'Takes about a minute.',
  'Tar om lag ett minutt.'
);

export const LOADING_TEXT = pick(
  'Loading the measure ...',
  'Henter målingen ...'
);
export const UNAVAILABLE_TEXT = pick(
  'Could not load the measure right now.',
  'Kunne ikke hente målingen akkurat nå.'
);
export const SUBMIT_FAILED_TEXT = pick(
  'The answers were not sent. Try again.',
  'Svarene ble ikke sendt. Prøv igjen.'
);
export const SUBMITTED_TEXT = pick(
  'Thank you, your answers are saved.',
  'Takk, svarene dine er lagret.'
);
export const CONSENT_SAVED_TEXT = pick(
  'Your choice is saved.',
  'Valget ditt er lagret.'
);
export const CONSENT_FAILED_TEXT = pick(
  'The choice was not saved. Try again.',
  'Valget ble ikke lagret. Prøv igjen.'
);

export const OWN_ANSWERS_TITLE = (window: AssessmentWindow): string =>
  pick(
    `Your answers ${WINDOW_LABEL[window].toLowerCase()}`,
    `Svarene dine ${WINDOW_LABEL[window].toLowerCase()}`
  );

export const CHANGE_TITLE = pick(
  'Change from the start to after the programme',
  'Endring fra oppstart til etter programmet'
);
export const CHANGE_TEXT = pick(
  'Counted for those who answered both times. Shown once at least five have.',
  'Regnes for dem som har svart begge ganger. Vises når minst fem har gjort det.'
);

export const LEGEND_TEXT = pick(
  'What do the numbers mean? Low is 3 to 6, middle 7 to 10, high 11 to 15. In SHoT 2026, 18.5 percent of students said they often lack someone to be with.',
  'Hva betyr tallene? Lav er 3 til 6, middels 7 til 10, høy 11 til 15. I SHoT 2026 svarte 18,5 prosent av studentene at de ofte savner noen å være sammen med.'
);

/** The bar's spoken form: how many answered in each band. */
export const bandsLabel = (bands: Record<ScoreBand, number>): string =>
  pick(
    `Distribution: ${bands.low} low, ${bands.middle} middle, ${bands.high} high`,
    `Fordeling: ${bands.low} lav, ${bands.middle} middels, ${bands.high} høy`
  );

export const bothTimesText = (n: number): string =>
  pick(`${n} answered both times.`, `${n} har svart begge ganger.`);

export const withheldText = (minN: number): string =>
  pick(
    `Shown once at least ${minN} have answered.`,
    `Vises når minst ${minN} har svart.`
  );

export const answeredText = (n: number, members: number): string =>
  pick(`${n} of ${members} have answered`, `${n} av ${members} har svart`);

export const notOpenText = pick('Not open yet', 'Ikke åpnet ennå');
export const openText = pick('Open for answers', 'Åpen for svar');

/** "8.6" from 8.57 (or "8,6" in Norwegian): one decimal. */
export const formatMean = (mean: number): string => {
  const text = (Math.round(mean * 10) / 10).toFixed(1);
  return contentLanguage() === 'nb' ? text.replace('.', ',') : text;
};

/** "+1.2" or "-0.8" for a change; a change of zero reads "0.0". */
export const formatChange = (change: number): string => {
  const text = formatMean(Math.abs(change));
  if (Math.round(change * 10) === 0) {
    return formatMean(0);
  }
  return change > 0 ? `+${text}` : `-${text}`;
};

export type ScoreBand = 'low' | 'middle' | 'high';

export const BAND_LABEL: Record<ScoreBand, string> = pick(
  { low: 'Low', middle: 'Middle', high: 'High' },
  { low: 'Lav', middle: 'Middels', high: 'Høy' }
);

/**
 * Low, middle or high for a total score: the range cut in thirds, the top
 * third taking the remainder (3 to 6, 7 to 10, 11 to 15 for the 3 to 15 scale).
 */
export const scoreBand = (
  score: number,
  scoreMin: number,
  scoreMax: number
): ScoreBand => {
  const third = Math.floor((scoreMax - scoreMin + 1) / 3);
  if (score < scoreMin + third) {
    return 'low';
  }
  if (score < scoreMin + 2 * third) {
    return 'middle';
  }
  return 'high';
};

/** How many answered in each band, from the summary's count per score. */
export const bandCounts = (
  distribution: Record<string, number>,
  scoreMin: number,
  scoreMax: number
): Record<ScoreBand, number> => {
  const counts: Record<ScoreBand, number> = { low: 0, middle: 0, high: 0 };
  Object.entries(distribution).forEach(([score, count]) => {
    const value = Number(score);
    if (!Number.isFinite(value)) {
      return;
    }
    counts[scoreBand(value, scoreMin, scoreMax)] += count;
  });
  return counts;
};

/** The answers a member can give next: the first shown window without an answer. */
export const nextWindow = (
  responses: readonly AssessmentOwnResponse[]
): AssessmentWindow | null => {
  const answered = new Set(responses.map((response) => response.window));
  return SHOWN_WINDOWS.find((window) => !answered.has(window)) ?? null;
};

export const responseFor = (
  responses: readonly AssessmentOwnResponse[],
  window: AssessmentWindow
): AssessmentOwnResponse | undefined =>
  responses.find((response) => response.window === window);

/** The scale word for a member's answer, for example "Sometimes". */
export const answerLabel = (
  instrument: AssessmentInstrument,
  value: number
): string =>
  instrument.scale.find((point) => point.value === value)?.label ??
  String(value);

/**
 * The question in its short form for the member's own-answers list, in
 * either language: "How often do you feel left out?" becomes "Left out".
 */
export const shortItemText = (text: string): string =>
  text
    .replace(/^How often do you feel that you\s+/i, '')
    .replace(/^How often do you feel\s+/i, '')
    .replace(/^Hvor ofte føler du at du\s+/i, '')
    .replace(/^Hvor ofte føler du deg\s+/i, '')
    .replace(/\?$/, '')
    .replace(/^\S/, (first) => first.toUpperCase());
